/**
 * Change History entry builder and sanitisation.
 * One complete source change or recorded activity → one permanent Change ID.
 */

export const CHANGE_HISTORY_DISPLAY = {
  NONE: 'None',
  NOT_APPLICABLE: 'Not Applicable',
  NOT_APPLICABLE_NEW_ITEM: 'Not Applicable — new item',
  NOT_REQUIRED: 'Not Required',
  PROTECTED_CREDENTIAL: 'Protected credential: Updated',
};

/** Permission module keys used for ordinary Team Member filtering of central history. */
export const CHANGE_HISTORY_MODULE_PERMISSION = {
  'Configuration — Limits & Thresholds': 'configuration',
  'Configuration — Retention': 'configuration',
  'Configuration — Routing': 'configuration',
  'Configuration — Permissions & Access': 'configuration',
  'Configuration — Validation': 'configuration',
  'Configuration — Publishing': 'configuration',
  'Configuration — Automation': 'configuration',
  'Configuration — Notifications': 'configuration',
  'Configuration — Appeals': 'configuration',
  'Configuration — Content Controls': 'configuration',
  'Configuration — Case Actions': 'configuration',
  'Configuration — Workflow': 'configuration',
  'Configuration — Lifecycle': 'configuration',
  'Configuration — Flagging': 'configuration',
  'Configuration — Flag': 'configuration',
  'Configuration — Compliance': 'configuration',
  'Configuration — Quote Matching & Distribution': 'configuration',
  'Configuration — Search Matching': 'configuration',
  'Configuration — Keywords': 'keywords_management',
  Settings: 'settings',
  'Team Members': 'team_members',
  Customers: 'customers',
  Businesses: 'businesses',
  Posts: 'posts_management',
  Reviews: 'reviews_management',
  Messages: 'messages',
  Quotes: 'quotes',
  'Support Tickets': 'support_tickets',
  Moderation: 'moderation',
  Compliance: 'compliance',
  'Fraud & Security': 'fraud_security',
  Appeals: 'appeals',
  'Billing & Subscriptions': 'billing_subscriptions',
  'Pricing Plans': 'pricing_plans',
  'Trade Categories': 'trade_categories_management',
  Keywords: 'keywords_management',
  'Form Inventory': 'form_inventory',
  'Licences, Badges & Tickets': 'compliance_items_management',
};

const PROTECTED_KEY_PATTERN =
  /password|temporary.?password|mfa|otp|verification.?code|secret|token|api.?key|session|bank|card.?number|cvv|ssn/i;

/**
 * Format a values map or string into safe list/detail display text.
 */
export function formatChangeValues(value) {
  if (value == null || value === '') return CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    if (!value.length) return CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE;
    return value
      .map((item) => (typeof item === 'string' ? item : formatChangeValues(item)))
      .filter(Boolean)
      .join('\n');
  }
  if (typeof value === 'object') {
    const lines = Object.entries(value).map(([key, raw]) => {
      const safe = sanitiseFieldValue(key, raw);
      if (safe === CHANGE_HISTORY_DISPLAY.NONE) return `${key}: ${CHANGE_HISTORY_DISPLAY.NONE}`;
      return `${key}: ${safe}`;
    });
    return lines.length ? lines.join('\n') : CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE;
  }
  return String(value);
}

export function sanitiseFieldValue(fieldKey, raw) {
  if (raw == null || raw === '') return CHANGE_HISTORY_DISPLAY.NONE;
  if (PROTECTED_KEY_PATTERN.test(String(fieldKey || ''))) {
    return CHANGE_HISTORY_DISPLAY.PROTECTED_CREDENTIAL;
  }
  if (typeof raw === 'string' && PROTECTED_KEY_PATTERN.test(raw) && raw.length > 40) {
    return CHANGE_HISTORY_DISPLAY.PROTECTED_CREDENTIAL;
  }
  if (typeof raw === 'object') return formatChangeValues(raw);
  return String(raw);
}

export function sanitiseValuesMap(map) {
  if (map == null) return null;
  if (typeof map === 'string') return map;
  if (typeof map !== 'object') return String(map);
  const out = {};
  Object.entries(map).forEach(([key, value]) => {
    out[key] = sanitiseFieldValue(key, value);
  });
  return out;
}

/**
 * Display label for Module column — drop parent prefix (e.g. "Configuration — ").
 * "Configuration — Limits & Thresholds" → "Limits & Thresholds"
 */
export function formatModuleDisplay(moduleLabel) {
  if (moduleLabel == null || moduleLabel === '') return CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE;
  const label = String(moduleLabel);
  const separatorIndex = label.indexOf(' — ');
  if (separatorIndex === -1) return label;
  const leaf = label.slice(separatorIndex + 3).trim();
  return leaf || label;
}

/**
 * Changed By shows the person name only (e.g. "Saim Sahi"), not role labels.
 * Strips prefixes like "Platform Owner — " from stored strings.
 */
export function formatChangedBy(actor) {
  if (!actor) return 'System';
  if (typeof actor === 'string') {
    const value = actor.trim();
    if (!value) return 'System';
    const separatorIndex = value.indexOf(' — ');
    if (separatorIndex !== -1) {
      const name = value.slice(separatorIndex + 3).trim();
      return name || value;
    }
    return value;
  }
  const name = actor.name || actor.displayName || '';
  if (name) return name;
  if (actor.isSystem) return 'System';
  if (actor.isVisitor) return actor.visitorLabel || 'Visitor';
  return 'System';
}

export function formatChangedOnDisplay(isoOrDate) {
  const date = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (Number.isNaN(date.getTime())) return CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE;
  const datePart = date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart}, ${timePart}`;
}

/**
 * Build a normalised Change History entry (without allocating Change ID).
 * Call via changeHistoryStore.recordChangeHistory / appendChangeHistoryEntry.
 */
export function buildChangeHistoryEntry(input = {}) {
  const changeType = input.changeType || input.action || 'Edit';
  const isAdd = String(changeType).toLowerCase() === 'add' || String(changeType).toLowerCase() === 'created';
  const isActivityOnly =
    Boolean(input.activityOnly) ||
    (input.previousValues == null &&
      input.newValues == null &&
      !isAdd &&
      Boolean(input.activityDetails));

  let previousValues = input.previousValues;
  let newValues = input.newValues;

  if (isAdd) {
    previousValues = CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE_NEW_ITEM;
    newValues =
      newValues == null
        ? CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE
        : formatChangeValues(sanitiseValuesMap(newValues) ?? newValues);
  } else if (isActivityOnly) {
    previousValues = CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE;
    newValues = CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE;
  } else {
    previousValues =
      previousValues == null
        ? CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE
        : formatChangeValues(sanitiseValuesMap(previousValues) ?? previousValues);
    newValues =
      newValues == null
        ? CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE
        : formatChangeValues(sanitiseValuesMap(newValues) ?? newValues);
  }

  const changedOnIso = input.changedOnIso || input.timestamp || new Date().toISOString();
  const changedOn =
    input.changedOn || formatChangedOnDisplay(changedOnIso);

  const changeReason =
    input.changeReason == null || input.changeReason === ''
      ? input.changeReasonRequired === false || input.changeReason == null
        ? CHANGE_HISTORY_DISPLAY.NOT_REQUIRED
        : CHANGE_HISTORY_DISPLAY.NOT_REQUIRED
      : String(input.changeReason);

  const moduleLabel = input.module || 'Settings';
  const sourceModuleRef = input.sourceModuleRef || moduleLabel;
  const sourceItemRef = input.sourceItemRef || input.itemId || null;
  const sourceChangeRef =
    input.sourceChangeRef ||
    `${sourceModuleRef}::${sourceItemRef || 'item'}::${changeType}::${changedOnIso}`;

  return {
    changeId: input.changeId || null,
    changedOn,
    changedOnIso,
    module: moduleLabel,
    changedItem: input.changedItem || CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE,
    itemId:
      input.itemId == null || input.itemId === ''
        ? CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE
        : String(input.itemId),
    changeType,
    previousValues,
    newValues,
    changeReason,
    changedBy: formatChangedBy(input.changedBy || input.actor),
    activityDetails: input.activityDetails || CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE,
    sourceChangeRef,
    sourceModuleRef,
    sourceItemRef: sourceItemRef != null ? String(sourceItemRef) : null,
    relatedRecord: input.relatedRecord || null,
    accountRef: input.accountRef || null,
    ticketRef: input.ticketRef || null,
    caseRef: input.caseRef || null,
    decisionRef: input.decisionRef || null,
    deleted: Boolean(input.deleted),
  };
}

/**
 * Project a central entry into local Audit Log columns:
 * Date and Time · Event · Details · Related Record
 */
export function toLocalAuditLogRow(entry) {
  if (!entry) return null;
  const detailParts = [];
  if (entry.changedBy) detailParts.push(entry.changedBy);
  if (
    entry.activityDetails &&
    entry.activityDetails !== CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE
  ) {
    detailParts.push(entry.activityDetails);
  } else if (
    entry.previousValues &&
    entry.previousValues !== CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE &&
    entry.previousValues !== CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE_NEW_ITEM
  ) {
    detailParts.push(`${entry.previousValues} → ${entry.newValues}`);
  } else if (
    entry.newValues &&
    entry.newValues !== CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE
  ) {
    detailParts.push(entry.newValues);
  }
  if (
    entry.changeReason &&
    entry.changeReason !== CHANGE_HISTORY_DISPLAY.NOT_REQUIRED
  ) {
    detailParts.push(entry.changeReason);
  }

  const related =
    entry.relatedRecord ||
    entry.decisionRef ||
    entry.caseRef ||
    entry.ticketRef ||
    (entry.itemId !== CHANGE_HISTORY_DISPLAY.NOT_APPLICABLE ? entry.itemId : null) ||
    '—';

  return {
    id: entry.changeId,
    changeId: entry.changeId,
    dateTime: entry.changedOn,
    timestamp: entry.changedOnIso,
    event: entry.changeType,
    action: entry.changeType,
    details: detailParts.filter(Boolean).join(' · ') || '—',
    relatedRecord: related,
    record: related,
    performedBy: entry.changedBy,
    summary: entry.activityDetails,
    sourceChangeRef: entry.sourceChangeRef,
    sourceModuleRef: entry.sourceModuleRef,
    sourceItemRef: entry.sourceItemRef,
  };
}

export function permissionModuleForHistoryEntry(entry) {
  if (!entry) return null;
  if (CHANGE_HISTORY_MODULE_PERMISSION[entry.module]) {
    return CHANGE_HISTORY_MODULE_PERMISSION[entry.module];
  }
  const moduleLabel = String(entry.module || '');
  if (moduleLabel.startsWith('Configuration')) return 'configuration';
  if (moduleLabel.startsWith('Settings')) return 'settings';
  return CHANGE_HISTORY_MODULE_PERMISSION[entry.sourceModuleRef] || null;
}
